import { log } from './logger';

export interface ValidationContext {
  business_area?: string;
  use_case?: string;
  document_class?: string;
  processor_id?: string;
  processor_type?: string;
  [key: string]: unknown;
}

export interface ValidationResult {
  isValid: boolean;
  cleanedValue: string;
  errors: string[];
  warnings: string[];
  metadata: Record<string, unknown>;
}

export interface RuleTraceEntry {
  scope_id?: string;
  rule_id?: string;
  priority?: number;
  scope_description?: string | null;
  rule_description?: string | null;
}

export interface FieldValidationResponse extends ValidationResult {
  fieldType: string;
  originalValue: string;
  ruleTrace: RuleTraceEntry[];
}

type CleaningStepConfig = { type: string; [key: string]: unknown };
type ValidationCheckConfig = { type: string; [key: string]: unknown };
type MergeStrategy = string | { cleaning?: string; validation?: string };

interface RulePreset {
  presetId: string;
  cleaningSteps: CleaningStepConfig[];
  validationChecks: ValidationCheckConfig[];
  metadata: Record<string, unknown>;
}

const ANY_MATCH_TOKENS = new Set(["*", "ANY", "any"]);

const asArray = (value: unknown): unknown[] => {
  if (value === null || value === undefined) {
    return [];
  }
  if (Array.isArray(value)) {
    return [...value];
  }
  if (value instanceof Set) {
    return Array.from(value);
  }
  return [value];
};

const extractContextValue = (
  context: Record<string, unknown>,
  path: string
): unknown => {
  const segments = path.split(".");
  let current: unknown = context;
  for (const segment of segments) {
    if (typeof current !== "object" || current === null) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
};

const valueMatchesRequirement = (
  value: unknown,
  requirement: unknown
): boolean => {
  if (
    requirement === null ||
    requirement === undefined ||
    (typeof requirement === "string" && ANY_MATCH_TOKENS.has(requirement)) ||
    (typeof requirement === "object" &&
      requirement !== null &&
      !Array.isArray(requirement) &&
      "any" in requirement &&
      (requirement as Record<string, unknown>).any === true)
  ) {
    return true;
  }

  if (
    typeof requirement === "object" &&
    requirement !== null &&
    !Array.isArray(requirement)
  ) {
    const requirementMap = requirement as Record<string, unknown>;

    if ("exists" in requirementMap) {
      const existsFlag = requirementMap.exists;
      if (existsFlag === true && (value === undefined || value === null)) {
        return false;
      }
      if (existsFlag === false && value !== undefined && value !== null) {
        return false;
      }
    }

    if ("any_of" in requirementMap) {
      const allowed = asArray(requirementMap.any_of);
      if (value === undefined || value === null) {
        if (!allowed.includes(null) && !allowed.includes(undefined)) {
          return false;
        }
      } else if (Array.isArray(value)) {
        const intersection = value.some((item) => allowed.includes(item));
        if (!intersection) {
          return false;
        }
      } else if (!allowed.includes(value)) {
        return false;
      }
    }

    if ("none_of" in requirementMap) {
      const blocked = asArray(requirementMap.none_of);
      if (Array.isArray(value)) {
        const hasBlocked = value.some((item) => blocked.includes(item));
        if (hasBlocked) {
          return false;
        }
      } else if (value !== undefined && value !== null) {
        if (blocked.includes(value)) {
          return false;
        }
      }
    }

    if ("equals" in requirementMap) {
      if (value !== requirementMap.equals) {
        return false;
      }
    }

    return true;
  }

  if (Array.isArray(requirement)) {
    if (value === undefined || value === null) {
      return requirement.includes(null) || requirement.includes(undefined);
    }
    if (Array.isArray(value)) {
      return value.some((item) => requirement.includes(item));
    }
    return requirement.includes(value);
  }

  return value === requirement;
};

const deepCopy = <T>(input: T): T => JSON.parse(JSON.stringify(input ?? null));

type CleaningStepResult = { value: string; warning?: string };
type CleaningExecutor = (
  value: string,
  config: Record<string, unknown>
) => CleaningStepResult;

const ensureString = (raw: unknown): string =>
  typeof raw === "string" ? raw : String(raw ?? "");

const ensureNumber = (raw: unknown): number | undefined => {
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === "string" && raw.trim() !== "" && !Number.isNaN(Number(raw))) {
    return Number(raw);
  }
  return undefined;
};

const cleaningExecutors: Record<string, CleaningExecutor> = {
  remove_non_digits: (value) => ({
    value: value ? value.replace(/\D+/g, "") : "",
  }),
  remove_special_chars: (value, config) => {
    const allowedPrefix = ensureString(config.allowed_prefix);
    if (!value) {
      return { value: "" };
    }
    if (allowedPrefix && value.startsWith(allowedPrefix)) {
      const rest = value.slice(allowedPrefix.length).replace(/\D+/g, "");
      return { value: allowedPrefix + rest };
    }
    // If no allowed prefix or value doesn't start with it, keep only digits
    // This prevents values starting with wrong letters from being accepted
    return { value: value.replace(/\D+/g, "") };
  },
  add_leading_zeros: (value, config) => {
    const length = ensureNumber(config.length);
    if (length === undefined) {
      return {
        value,
        warning: "Bereinigungsschritt 'add_leading_zeros' fehlt 'length' Konfiguration",
      };
    }
    if (!value) {
      return { value: "0".repeat(length) };
    }
    if (value.length >= length) {
      return { value };
    }
    return { value: value.padStart(length, "0") };
  },
  add_prefix: (value, config) => {
    const prefix = ensureString(config.prefix);
    if (!prefix) {
      return {
        value,
        warning: "Bereinigungsschritt 'add_prefix' fehlt 'prefix' Konfiguration",
      };
    }
    if (!value) {
      return { value: prefix };
    }
    if (value.startsWith(prefix)) {
      return { value };
    }
    return { value: prefix + value };
  },
  uppercase: (value) => ({ value: value ? value.toUpperCase() : "" }),
  lowercase: (value) => ({ value: value ? value.toLowerCase() : "" }),
  trim_whitespace: (value) => ({ value: value ? value.trim() : "" }),
  remove_spaces: (value) => ({ value: value ? value.replace(/\s+/g, "") : "" }),
  normalize_country_code: (value, config) => {
    if (!value) {
      return { value: "" };
    }
    const codeLength = ensureNumber(config.code_length) ?? 2;
    const prefix = value.slice(0, codeLength).toUpperCase();
    return { value: prefix + value.slice(codeLength) };
  },
  remove_prefix: (value, config) => {
    const prefix = ensureString(config.prefix);
    if (!prefix) {
      return {
        value,
        warning: "Bereinigungsschritt 'remove_prefix' fehlt 'prefix' Konfiguration",
      };
    }
    if (!value) {
      return { value: "" };
    }
    if (value.startsWith(prefix)) {
      return { value: value.slice(prefix.length) };
    }
    return { value };
  },
  pad_suffix_with_zeros: (value, config) => {
    const length = ensureNumber(config.length);
    if (length === undefined) {
      return {
        value,
        warning: "Bereinigungsschritt 'pad_suffix_with_zeros' fehlt 'length' Konfiguration",
      };
    }
    const prefix = ensureString(config.prefix);
    const workingValue = value ?? "";
    let suffix = workingValue;
    if (prefix && workingValue.startsWith(prefix)) {
      suffix = workingValue.slice(prefix.length);
    }
    let numericSuffix = suffix.replace(/\D+/g, "");
    if (!numericSuffix) {
      numericSuffix = "0".repeat(length);
    } else if (numericSuffix.length < length) {
      numericSuffix = numericSuffix.padStart(length, "0");
    }
    const padded = prefix ? prefix + numericSuffix : numericSuffix;
    return { value: padded };
  },
  extract_vnr_from_zusatznummer: (value) => {
    if (!value) {
      return { value: "" };
    }
    // Handle 11 digits: 9-digit VNR + Zusatznummer "00"
    if (value.length === 11 && value.endsWith("00")) {
      return {
        value: value.slice(0, 9),
        warning: "VNR enthielt Zusatznummer '00', nur VNR-Teil wird validiert",
      };
    }
    // Handle 12 digits: 10-digit VNR + Zusatznummer "00"
    if (value.length === 12 && value.endsWith("00")) {
      return {
        value: value.slice(0, 10),
        warning: "VNR enthielt Zusatznummer '00', nur VNR-Teil wird validiert",
      };
    }
    return { value };
  },
};

const calculateCheckDigitMethod1 = (numberWithoutCheck: string): string => {
  let total = 0;
  let weight = 2;
  for (let idx = numberWithoutCheck.length - 1; idx >= 0; idx -= 1) {
    const digit = Number(numberWithoutCheck[idx]);
    total += digit * weight;
    weight = weight < 7 ? weight + 1 : 2;
  }
  let checkDigit = 11 - (total % 11);
  if (checkDigit > 9) {
    checkDigit -= 10;
  }
  return String(checkDigit);
};

const mod97 = (numericString: string): number => {
  let remainder = 0;
  const chunkSize = 9;
  for (let index = 0; index < numericString.length; index += chunkSize) {
    const chunk = numericString.slice(index, index + chunkSize);
    const parsed = Number(`${remainder}${chunk}`);
    remainder = parsed % 97;
  }
  return remainder;
};

type ValidationExecutor = (
  value: string,
  config: Record<string, unknown>
) => { isValid: boolean; error?: string };

const validationExecutors: Record<string, ValidationExecutor> = {
  length: (value, config) => {
    const expected = ensureNumber(config.value);
    if (expected === undefined) {
      return { isValid: false, error: "Längenprüfung fehlt 'value' Konfiguration" };
    }
    return value.length === expected
      ? { isValid: true }
      : {
          isValid: false,
          error: `Erwartet genau ${expected} Zeichen, erhalten ${value.length}`,
        };
  },
  max_length: (value, config) => {
    const maxLength = ensureNumber(config.value);
    if (maxLength === undefined) {
      return { isValid: false, error: "Maximale Längenprüfung fehlt 'value' Konfiguration" };
    }
    return value.length <= maxLength
      ? { isValid: true }
      : {
          isValid: false,
          error: `Erwartet höchstens ${maxLength} Zeichen, erhalten ${value.length}`,
        };
  },
  min_length: (value, config) => {
    const minLength = ensureNumber(config.value);
    if (minLength === undefined) {
      return { isValid: false, error: "Minimale Längenprüfung fehlt 'value' Konfiguration" };
    }
    return value.length >= minLength
      ? { isValid: true }
      : {
          isValid: false,
          error: `Erwartet mindestens ${minLength} Zeichen, erhalten ${value.length}`,
        };
  },
  year_prefix: (value, config) => {
    const expectedPrefix = ensureString(config.value);
    if (!expectedPrefix) {
      return { isValid: false, error: "Jahrespräfix-Prüfung fehlt 'value' Konfiguration" };
    }
    const prefixLength = ensureNumber(config.length) ?? expectedPrefix.length;
    if (value.length < prefixLength) {
      return { isValid: false, error: "Wert zu kurz für Jahrespräfix-Prüfung" };
    }
    const actualPrefix = value.slice(0, prefixLength);
    return actualPrefix === expectedPrefix
      ? { isValid: true }
      : {
          isValid: false,
          error: `Jahr muss mit '${expectedPrefix}' beginnen, erhalten '${actualPrefix}'`,
        };
  },
  position_range: (value, config) => {
    const startPos = ensureNumber(config.start);
    const endPos = ensureNumber(config.end);
    const expected = ensureString(config.value);
    if (startPos === undefined || endPos === undefined || expected === undefined) {
      return {
        isValid: false,
        error: "Positionsbereich-Prüfung fehlt 'start', 'end' oder 'value' Konfiguration",
      };
    }
    const startIdx = Math.max(0, startPos - 1);
    const endIdx = Math.min(value.length, endPos);
    if (endIdx > value.length) {
      return {
        isValid: false,
        error: `Wert zu kurz für Positionsbereich-Prüfung (benötigt mindestens ${endIdx} Zeichen)`,
      };
    }
    const actual = value.slice(startIdx, endIdx);
    return actual === expected
      ? { isValid: true }
      : {
          isValid: false,
          error: `Positionen ${startPos}-${endPos} müssen '${expected}' sein, erhalten '${actual}'`,
        };
  },
  check_digit: (value) => {
    if (!value) {
      return { isValid: false, error: "Leerer Wert für Prüfziffernvalidierung" };
    }
    if (!/^\d+$/.test(value)) {
      return {
        isValid: false,
        error: "Wert darf nur Ziffern für Prüfziffernvalidierung enthalten",
      };
    }
    const numberWithoutCheck = value.slice(0, -1);
    const givenCheckDigit = value.slice(-1);
    const calculated = calculateCheckDigitMethod1(numberWithoutCheck);
    return calculated === givenCheckDigit
      ? { isValid: true }
      : {
          isValid: false,
          error: "Prüfziffernvalidierung fehlgeschlagen: Der Wert ist fehlerhaft, der Vorgang muss über Klärung gesteuert werden",
        };
  },
  mod97_check: (value) => {
    if (!value || value.length < 4) {
      return { isValid: false, error: "IBAN zu kurz für Modulo-97-Validierung" };
    }
    const rearranged = value.slice(4) + value.slice(0, 4);
    let numericString = "";
    for (const char of rearranged) {
      if (/[A-Za-z]/.test(char)) {
        numericString += String(char.toUpperCase().charCodeAt(0) - 55);
      } else {
        numericString += char;
      }
    }
    if (!/^\d+$/.test(numericString)) {
      return {
        isValid: false,
        error: "IBAN enthält ungültige Zeichen für Modulo-97-Berechnung",
      };
    }
    return mod97(numericString) === 1
      ? { isValid: true }
      : { isValid: false, error: "IBAN Modulo-97-Prüfung fehlgeschlagen (Ergebnis nicht gleich 1)" };
  },
  country_code: (value, config) => {
    const expected = ensureString(config.value);
    if (!expected) {
      return { isValid: false, error: "Ländercode-Prüfung fehlt 'value' Konfiguration" };
    }
    if (value.length < expected.length) {
      return { isValid: false, error: "Wert zu kurz für Ländercode-Prüfung" };
    }
    const actual = value.slice(0, expected.length).toUpperCase();
    return actual === expected.toUpperCase()
      ? { isValid: true }
      : { isValid: false, error: `Erwarteter Ländercode '${expected}', erhalten '${actual}'` };
  },
  regex_match: (value, config) => {
    const pattern = ensureString(config.pattern);
    if (!pattern) {
      return {
        isValid: false,
        error: "Regex-Übereinstimmungsprüfung fehlt 'pattern' Konfiguration",
      };
    }
    try {
      const regex = new RegExp(pattern);
      return regex.test(value)
        ? { isValid: true }
        : { isValid: false, error: `Wert stimmt nicht mit Muster überein: ${pattern}` };
    } catch (error) {
      return { isValid: false, error: `Ungültiges Regex-Muster: ${String(error)}` };
    }
  },
  all_digits: (value) => {
    return /^\d+$/.test(value)
      ? { isValid: true }
      : { isValid: false, error: "Wert darf nur Ziffern enthalten" };
  },
  valid_currency_amount: (value) => {
    if (!value || value.trim() === "") {
      return { isValid: false, error: "Betrag darf nicht leer sein" };
    }

    const cleaned = value.trim();

    // Reject invalid separators (OCR errors: : ; /)
    if (/[:|;/]/.test(cleaned)) {
      return {
        isValid: false,
        error: "Ungültiges Zeichen im Betrag (verwenden Sie ',' als Dezimaltrennzeichen)"
      };
    }

    // Reject period (no thousands separators allowed, only comma as decimal)
    if (cleaned.includes('.')) {
      return {
        isValid: false,
        error: "Ungültiges Format: Tausendertrennzeichen nicht erlaubt (Format: z.B. 1234,56)"
      };
    }

    // Check for multiple commas
    const commaCount = (cleaned.match(/,/g) || []).length;
    if (commaCount > 1) {
      return {
        isValid: false,
        error: "Ungültiger Betrag: mehrere Dezimalkommas gefunden"
      };
    }

    // Validate final pattern: only digits, optional comma with 1-2 decimal places
    // Format: 1234 or 1234,56 or -1234,56
    const validCurrencyPattern = /^-?\d+(?:,\d{1,2})?$/;

    if (!validCurrencyPattern.test(cleaned)) {
      return {
        isValid: false,
        error: "Ungültiges Betragsformat (erwartetes Format: z.B. 1234,56)"
      };
    }

    return { isValid: true };
  },
};

const applyCleaningSteps = (
  rawValue: string,
  steps: CleaningStepConfig[]
): { value: string; warnings: string[] } => {
  let value = typeof rawValue === "string" ? rawValue : String(rawValue ?? "");
  const warnings: string[] = [];

  for (const step of steps) {
    const stepType = typeof step.type === "string" ? step.type : "";
    const executor = cleaningExecutors[stepType];
    if (!executor) {
      warnings.push(`Unbekannter Bereinigungsschritt '${stepType}'`);
      continue;
    }
    try {
      const result = executor(value, step);
      value = result.value;
      if (result.warning) {
        warnings.push(result.warning);
      }
    } catch (error) {
      warnings.push(`Bereinigungsschritt '${stepType}' fehlgeschlagen: ${String(error)}`);
    }
  }

  return { value, warnings };
};

const applyValidationChecks = (
  value: string,
  checks: ValidationCheckConfig[]
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  for (const check of checks) {
    const checkType = typeof check.type === "string" ? check.type : "";
    const executor = validationExecutors[checkType];
    if (!executor) {
      errors.push(`Unbekannte Validierungsprüfung '${checkType}'`);
      continue;
    }
    try {
      const result = executor(value, check);
      if (!result.isValid && result.error) {
        errors.push(result.error);
      }
    } catch (error) {
      errors.push(`Validierungsprüfung '${checkType}' fehlgeschlagen: ${String(error)}`);
    }
  }

  return { isValid: errors.length === 0, errors };
};

const normalizeMergeStrategy = (
  mergeStrategy?: MergeStrategy
): [string, string] => {
  if (mergeStrategy && typeof mergeStrategy === "object") {
    const cleaning = String(mergeStrategy.cleaning ?? "append");
    const validation = String(mergeStrategy.validation ?? "append");
    return [cleaning, validation];
  }
  const strategy = String(mergeStrategy ?? "append");
  return [strategy, strategy];
};

const applyWithStrategy = <T>(
  base: T[],
  additions: T[],
  strategy: string
): T[] => {
  if (!additions || additions.length === 0) {
    return base;
  }
  if (strategy === "replace" || strategy === "replace_all") {
    return deepCopy(additions);
  }
  if (strategy === "prepend") {
    return [...deepCopy(additions), ...base];
  }
  return [...base, ...deepCopy(additions)];
};

class FieldRuleDefinition {
  readonly ruleId: string;
  readonly fieldTypes: Set<string>;
  readonly presets: string[];
  readonly cleaningSteps: CleaningStepConfig[];
  readonly validationChecks: ValidationCheckConfig[];
  readonly mergeStrategy: MergeStrategy;
  readonly options: Record<string, unknown>;
  readonly description?: string | null;
  readonly metadata: Record<string, unknown>;

  constructor(params: {
    ruleId: string;
    fieldTypes: Iterable<string>;
    presets?: string[];
    cleaningSteps?: CleaningStepConfig[];
    validationChecks?: ValidationCheckConfig[];
    mergeStrategy?: MergeStrategy;
    options?: Record<string, unknown>;
    description?: string | null;
    metadata?: Record<string, unknown>;
  }) {
    this.ruleId = params.ruleId;
    this.fieldTypes = new Set(params.fieldTypes);
    this.presets = params.presets ?? [];
    this.cleaningSteps = params.cleaningSteps ?? [];
    this.validationChecks = params.validationChecks ?? [];
    this.mergeStrategy = params.mergeStrategy ?? "append";
    this.options = params.options ?? {};
    this.description = params.description;
    this.metadata = params.metadata ?? {};
  }

  appliesTo(fieldType: string): boolean {
    return this.fieldTypes.has("*") || this.fieldTypes.has(fieldType);
  }
}

class RuleScope {
  readonly scopeId: string;
  readonly priority: number;
  readonly conditions: Record<string, unknown>;
  readonly fieldRules: FieldRuleDefinition[];
  readonly enabled: boolean;
  readonly description?: string | null;
  readonly metadata: Record<string, unknown>;

  constructor(params: {
    scopeId: string;
    priority: number;
    conditions: Record<string, unknown>;
    fieldRules: FieldRuleDefinition[];
    enabled?: boolean;
    description?: string | null;
    metadata?: Record<string, unknown>;
  }) {
    this.scopeId = params.scopeId;
    this.priority = params.priority;
    this.conditions = params.conditions;
    this.fieldRules = params.fieldRules;
    this.enabled = params.enabled ?? true;
    this.description = params.description;
    this.metadata = params.metadata ?? {};
  }

  matchesContext(context: Record<string, unknown>): boolean {
    if (!this.enabled) {
      return false;
    }
    return Object.entries(this.conditions).every(([key, requirement]) => {
      const value = extractContextValue(context, key);
      return valueMatchesRequirement(value, requirement);
    });
  }

  iterFieldRules(fieldType: string): FieldRuleDefinition[] {
    return this.fieldRules.filter((rule) => rule.appliesTo(fieldType));
  }
}

type AggregatedRule = {
  cleaning_steps: CleaningStepConfig[];
  validation_checks: ValidationCheckConfig[];
  options: Record<string, unknown>;
  rule_trace: RuleTraceEntry[];
  presets_applied: string[];
  metadata: Record<string, unknown>;
  source: string;
};

type ConfigLoadParams = {
  accessToken: string;
  idToken: string;
  environment: string;
  projectId: string;
};

const buildScopeId = (parts: (string | undefined)[]): string =>
  parts.filter((part) => Boolean(part)).join("-");

const mergeMetadata = (
  base?: Record<string, unknown> | null,
  extra?: Record<string, unknown> | null
): Record<string, unknown> => {
  const merged: Record<string, unknown> = {};
  if (base && typeof base === "object") {
    Object.assign(merged, base);
  }
  if (extra && typeof extra === "object") {
    Object.assign(merged, extra);
  }
  return merged;
};

const fetchValidationConfig = async (
  params: ConfigLoadParams
): Promise<Record<string, unknown> | undefined> => {
  const { accessToken, idToken, environment, projectId } = params;
  const documentId = "configuration";
  const url = `${process.env.INTERNAL_API_URL}/documents/validation_rules/${documentId}`;

  log.info("Fetching validation config", {
    documentId,
    url
  });

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  log.debug("Validation API HTTP response", {
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
    headers: Object.fromEntries(response.headers.entries())
  });

  if (response.status === 404) {
    console.warn("Validation rules not found (404)");
    return undefined;
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Validation API error response", {
      status: response.status,
      statusText: response.statusText,
      body: errorText
    });
    throw new Error(
      `Validierungskonfiguration konnte nicht geladen werden (${response.status} ${response.statusText})`
    );
  }

  const responseText = await response.text();
  log.debug("Validation API response body (raw)", {
    length: responseText.length,
    first100Chars: responseText.substring(0, 100),
    fullText: responseText
  });

  if (!responseText || responseText.trim() === "") {
    console.warn("Validation API returned empty body");
    return undefined;
  }

  const document = JSON.parse(responseText);
  log.debug("Validation API response structure", {
    hasDocument: !!document,
    documentKeys: document ? Object.keys(document) : [],
    documentType: typeof document,
    fullDocument: document
  });

  if (!document) {
    console.warn("API returned null/empty document for validation rules");
    return undefined;
  }

  // Handle both Firestore REST API format and plain dict format
  const fields = document.fields ?? {};
  let configJson = fields.config_json?.stringValue ?? fields.config?.stringValue;

  // If not in REST API format, try plain format
  if (!configJson) {
    configJson = document.config_json ?? document.config;
  }

  if (!configJson) {
    log.warn("No config_json found in document", {
      documentKeys: Object.keys(document),
      hasFields: !!document.fields,
      hasConfigJson: !!(document.config_json || document.config)
    });
    return undefined;
  }

  const config = JSON.parse(configJson) as Record<string, unknown>;

  // Try to get version from Firestore fields or plain format
  if (!config.version) {
    config.version = fields.version?.stringValue ?? document.version;
  }

  // Try to get metadata from Firestore fields or plain format
  if (!config.metadata) {
    const metadataJson = fields.metadata_json?.stringValue ?? document.metadata_json;
    if (metadataJson) {
      config.metadata = JSON.parse(metadataJson);
    }
  }

  log.info("Parsed validation config", {
    version: config.version,
    hasPresets: !!(config as any).presets,
    hasScopes: !!(config as any).scopes,
    configKeys: Object.keys(config)
  });

  return config;
};

class FrontendValidationRuleEngine {
  private presets = new Map<string, RulePreset>();
  private scopes: RuleScope[] = [];
  private configuredFieldTypes = new Set<string>();
  private configVersion?: string;
  private configMetadata: Record<string, unknown> = {};
  private loadKey?: string;
  private loadPromise?: Promise<void>;

  async ensureConfigLoaded(params: ConfigLoadParams): Promise<void> {
    const { environment, projectId } = params;
    if (!environment) {
      throw new Error(
        "Umgebung nicht für Validierungskonfiguration angegeben"
      );
    }
    if (!projectId) {
      throw new Error(
        "Projekt-ID nicht für Validierungskonfiguration angegeben"
      );
    }

    const key = `${projectId}::${environment}`;

    if (this.loadKey === key && this.scopes.length > 0) {
      log.debug("Validierungsregeln bereits geladen", {
        key,
        version: this.configVersion,
        scopesCount: this.scopes.length,
        presetsCount: this.presets.size,
      });
      return;
    }

    if (!this.loadPromise) {
      this.loadPromise = (async () => {
        log.debug("Lade Validierungsregeln von Firestore", {
          environment,
          projectId,
        });
        const config = await fetchValidationConfig(params);
        if (!config) {
          console.warn("Keine Validierungsregeln in Firestore gefunden", {
            environment,
            projectId,
          });
        } else {
          log.debug("Validierungsregeln erfolgreich geladen", {
            version: (config as any).version,
            hasPresets: !!(config as any).presets,
            hasBusinessAreas: !!(config as any).business_areas,
          });
        }
        this.applyConfiguration(config ?? {});
        this.loadKey = key;
        log.debug("Validierungsregeln angewendet", {
          key,
          version: this.configVersion,
          scopesCount: this.scopes.length,
          presetsCount: this.presets.size,
          configuredFields: Array.from(this.configuredFieldTypes),
        });
      })().finally(() => {
        this.loadPromise = undefined;
      });
    }

    await this.loadPromise;
  }

  getConfigVersion(): string | undefined {
    return this.configVersion;
  }

  hasRuleForField(fieldType: string): boolean {
    return (
      this.configuredFieldTypes.has("*") ||
      this.configuredFieldTypes.has(fieldType)
    );
  }

  validateField(
    fieldType: string,
    rawValue: string,
    baseContext: ValidationContext
  ): FieldValidationResponse {
    const context = this.buildContext(fieldType, baseContext);
    const rule = this.resolveRuleForContext(fieldType, context);
    if (!rule) {
      return {
        fieldType,
        originalValue: rawValue,
        cleanedValue: rawValue,
        isValid: true,
        errors: [],
        warnings: [],
        metadata: { source: "no_rule" },
        ruleTrace: [],
      };
    }

    const { cleaning_steps: cleaningSteps, validation_checks: validationChecks } =
      rule;

    const cleaningResult = applyCleaningSteps(rawValue ?? "", cleaningSteps);
    const validationResult = applyValidationChecks(
      cleaningResult.value,
      validationChecks
    );

    const errors = [...validationResult.errors];
    const warnings = [...cleaningResult.warnings];

    const dedupe = (items: string[]): string[] =>
      items.length > 1 ? Array.from(new Set(items)) : items;

    const finalErrors = dedupe(errors);
    const finalWarnings = dedupe(warnings);

    return {
      fieldType,
      originalValue: rawValue,
      cleanedValue: cleaningResult.value,
      isValid: validationResult.isValid,
      errors: finalErrors,
      warnings: finalWarnings,
      metadata: {
        config_version: this.configVersion,
        configuration_metadata: this.configMetadata,
        rule_metadata: rule.metadata,
      },
      ruleTrace: rule.rule_trace ?? [],
    };
  }

  private buildContext(
    fieldType: string,
    baseContext: ValidationContext
  ): Record<string, unknown> {
    return {
      field_type: fieldType,
      ...baseContext,
      extra: { ...baseContext },
    };
  }

  private resolveRuleForContext(
    fieldType: string,
    context: Record<string, unknown>
  ): AggregatedRule | undefined {
    const matched: Array<[RuleScope, FieldRuleDefinition]> = [];

    for (const scope of this.scopes) {
      if (!scope.matchesContext(context)) {
        continue;
      }
      const applicable = scope.iterFieldRules(fieldType);
      for (const rule of applicable) {
        matched.push([scope, rule]);
      }
    }

    if (matched.length === 0) {
      return undefined;
    }

    return this.assembleRulePayload(matched);
  }

  private assembleRulePayload(
    matched: Array<[RuleScope, FieldRuleDefinition]>
  ): AggregatedRule {
    let cleaningSteps: CleaningStepConfig[] = [];
    let validationChecks: ValidationCheckConfig[] = [];
    const options: Record<string, unknown> = { fallback_to_builtin: true };
    const ruleTrace: RuleTraceEntry[] = [];
    const presetsApplied: string[] = [];
    const appliedRulesMetadata: Record<string, unknown>[] = [];

    for (const [scope, rule] of matched) {
      const [cleaningStrategy, validationStrategy] = normalizeMergeStrategy(
        rule.mergeStrategy
      );

      if (cleaningStrategy === "replace" || cleaningStrategy === "replace_all") {
        cleaningSteps = [];
      }
      if (validationStrategy === "replace" || validationStrategy === "replace_all") {
        validationChecks = [];
      }

      for (const presetId of rule.presets) {
        const preset = this.presets.get(presetId);
        if (!preset) {
          console.warn(
            `Referenced validation preset '${presetId}' not found for scope '${scope.scopeId}'`
          );
          continue;
        }
        if (preset.cleaningSteps.length > 0) {
          cleaningSteps = applyWithStrategy(
            cleaningSteps,
            preset.cleaningSteps,
            cleaningStrategy
          );
        }
        if (preset.validationChecks.length > 0) {
          validationChecks = applyWithStrategy(
            validationChecks,
            preset.validationChecks,
            validationStrategy
          );
        }
        presetsApplied.push(presetId);
      }

      if (rule.cleaningSteps.length > 0) {
        cleaningSteps = applyWithStrategy(
          cleaningSteps,
          rule.cleaningSteps,
          cleaningStrategy
        );
      }
      if (rule.validationChecks.length > 0) {
        validationChecks = applyWithStrategy(
          validationChecks,
          rule.validationChecks,
          validationStrategy
        );
      }

      Object.assign(options, rule.options ?? {});

      ruleTrace.push({
        scope_id: scope.scopeId,
        rule_id: rule.ruleId,
        priority: scope.priority,
        scope_description: scope.description ?? null,
        rule_description: rule.description ?? null,
      });

      appliedRulesMetadata.push({
        scope_id: scope.scopeId,
        scope_priority: scope.priority,
        rule_id: rule.ruleId,
        scope_metadata: scope.metadata,
        rule_metadata: rule.metadata,
      });
    }

    const metadata: Record<string, unknown> = {
      config_version: this.configVersion,
      rules_applied: appliedRulesMetadata,
    };
    if (Object.keys(this.configMetadata).length > 0) {
      metadata.configuration_metadata = this.configMetadata;
    }

    return {
      cleaning_steps: cleaningSteps,
      validation_checks: validationChecks,
      options,
      rule_trace: ruleTrace,
      presets_applied: presetsApplied,
      metadata,
      source: "configuration",
    };
  }

  private applyConfiguration(configData: Record<string, unknown>): void {
    const payload = deepCopy(configData ?? {});
    this.presets.clear();
    this.scopes = [];
    this.configuredFieldTypes.clear();
    this.configVersion =
      typeof payload.version === "string" ? payload.version : undefined;
    this.configMetadata =
      (payload.metadata && typeof payload.metadata === "object"
        ? (payload.metadata as Record<string, unknown>)
        : {}) ?? {};

    const presets = (payload.presets as Record<string, unknown>) ?? {};
    for (const [presetId, presetConfigRaw] of Object.entries(presets)) {
      if (!presetConfigRaw || typeof presetConfigRaw !== "object") {
        continue;
      }
      const presetConfig = presetConfigRaw as Record<string, unknown>;
      const cleaningSteps =
        (presetConfig.cleaning_steps as CleaningStepConfig[]) ??
        (presetConfig.cleaningSteps as CleaningStepConfig[]) ??
        [];
      const validationChecks =
        (presetConfig.validation_checks as ValidationCheckConfig[]) ??
        (presetConfig.validationChecks as ValidationCheckConfig[]) ??
        [];
      const metadata =
        (presetConfig.metadata as Record<string, unknown>) ??
        (presetConfigRaw as Record<string, unknown>);

      this.presets.set(presetId, {
        presetId,
        cleaningSteps: deepCopy(cleaningSteps),
        validationChecks: deepCopy(validationChecks),
        metadata: deepCopy(metadata ?? {}),
      });
    }

    const compiledScopes: RuleScope[] = [];
    const scopesArray = (payload.scopes as Array<Record<string, unknown>>) ?? [];
    scopesArray.forEach((scopeConfig, index) => {
      if (!scopeConfig) {
        return;
      }
      const scopeId =
        (scopeConfig.id as string) ?? buildScopeId(["scope", String(index)]);
      const fieldRulesPayload =
        (scopeConfig.field_rules as Array<Record<string, unknown>>) ??
        (scopeConfig.fieldRules as Array<Record<string, unknown>>) ??
        [];
      const fieldRules = fieldRulesPayload
        .map((ruleConfig, ruleIndex) => {
          if (!ruleConfig) {
            return undefined;
          }
          const fieldTypesRaw =
            (ruleConfig.field_types as string[]) ??
            (ruleConfig.fieldTypes as string[]) ??
            [];
          const fieldTypes =
            fieldTypesRaw.length > 0 ? fieldTypesRaw : ["*"];
          const ruleId =
            (ruleConfig.id as string) ??
            buildScopeId([scopeId, "rule", String(ruleIndex)]);
          const definition = new FieldRuleDefinition({
            ruleId,
            fieldTypes,
            presets:
              (ruleConfig.presets as string[]) ??
              (ruleConfig.preset_ids as string[]) ??
              [],
            cleaningSteps:
              (ruleConfig.cleaning_steps as CleaningStepConfig[]) ??
              (ruleConfig.cleaningSteps as CleaningStepConfig[]) ??
              [],
            validationChecks:
              (ruleConfig.validation_checks as ValidationCheckConfig[]) ??
              (ruleConfig.validationChecks as ValidationCheckConfig[]) ??
              [],
            mergeStrategy:
              (ruleConfig.merge_strategy as MergeStrategy) ??
              ruleConfig.mergeStrategy,
            options:
              (ruleConfig.options as Record<string, unknown>) ?? {},
            description: (ruleConfig.description as string) ?? null,
            metadata:
              (ruleConfig.metadata as Record<string, unknown>) ?? {},
          });
          definition.fieldTypes.forEach((field) =>
            this.configuredFieldTypes.add(field)
          );
          return definition;
        })
        .filter(
          (rule): rule is FieldRuleDefinition => Boolean(rule)
        );

      if (fieldRules.length === 0) {
        return;
      }

      const scope = new RuleScope({
        scopeId,
        priority: Number(scopeConfig.priority ?? 100),
        conditions:
          (scopeConfig.conditions as Record<string, unknown>) ?? {},
        fieldRules,
        enabled: (scopeConfig.enabled as boolean) ?? true,
        description: (scopeConfig.description as string) ?? null,
        metadata:
          (scopeConfig.metadata as Record<string, unknown>) ?? {},
      });

      compiledScopes.push(scope);
    });

    this.buildScopesFromHierarchy(
      payload as Record<string, unknown>,
      compiledScopes
    );

    compiledScopes.sort((a, b) => a.priority - b.priority);
    this.scopes = compiledScopes;
  }

  private buildScopesFromHierarchy(
    config: Record<string, unknown>,
    compiledScopes: RuleScope[]
  ): void {
    const appendScope = (
      scopeId: string,
      priority: number,
      conditions: Record<string, unknown>,
      fieldMap: Record<string, unknown>,
      description?: string | null,
      metadata?: Record<string, unknown>
    ) => {
      const fieldRules = this.createFieldRulesFromMap(scopeId, fieldMap);
      if (fieldRules.length === 0) {
        return;
      }
      compiledScopes.push(
        new RuleScope({
          scopeId,
          priority,
          conditions,
          fieldRules,
          description,
          metadata,
        })
      );
    };

    const globalSection = (config.global as Record<string, unknown>) ?? {};
    if (globalSection.fields && typeof globalSection.fields === "object") {
      appendScope(
        "global-fields",
        100,
        {},
        globalSection.fields as Record<string, unknown>,
        (globalSection.description as string) ?? null,
        (globalSection.metadata as Record<string, unknown>) ?? {}
      );
    }

    const businessAreas =
      (config.business_areas as Record<string, Record<string, unknown>>) ?? {};
    for (const [businessArea, businessConfig] of Object.entries(
      businessAreas
    )) {
      const baseConditions = { business_area: [businessArea] };
      appendScope(
        `business-area-${businessArea}`,
        200,
        baseConditions,
        (businessConfig.fields as Record<string, unknown>) ?? {},
        (businessConfig.description as string) ?? null,
        mergeMetadata(businessConfig.metadata as Record<string, unknown>, {
          business_area: businessArea,
        })
      );

      const classes =
        (businessConfig.document_classes as Record<
          string,
          Record<string, unknown>
        >) ?? {};
      for (const [documentClass, classConfig] of Object.entries(classes)) {
        appendScope(
          `business-area-${businessArea}-class-${documentClass}`,
          230,
          {
            business_area: [businessArea],
            document_class: [documentClass],
          },
          (classConfig.fields as Record<string, unknown>) ?? {},
          (classConfig.description as string) ?? null,
          mergeMetadata(classConfig.metadata as Record<string, unknown>, {
            business_area: businessArea,
            document_class: documentClass,
          })
        );
      }

      const useCases =
        (businessConfig.use_cases as Record<string, Record<string, unknown>>) ??
        {};
      for (const [useCase, useCaseConfig] of Object.entries(useCases)) {
        appendScope(
          `business-area-${businessArea}-use-case-${useCase}`,
          240,
          {
            business_area: [businessArea],
            use_case: [useCase],
          },
          (useCaseConfig.fields as Record<string, unknown>) ?? {},
          (useCaseConfig.description as string) ?? null,
          mergeMetadata(useCaseConfig.metadata as Record<string, unknown>, {
            business_area: businessArea,
            use_case: useCase,
          })
        );
      }
    }

    const documentClasses =
      (config.document_classes as Record<string, Record<string, unknown>>) ?? {};
    for (const [documentClass, docConfig] of Object.entries(documentClasses)) {
      appendScope(
        `document-class-${documentClass}`,
        250,
        { document_class: [documentClass] },
        (docConfig.fields as Record<string, unknown>) ?? {},
        (docConfig.description as string) ?? null,
        mergeMetadata(docConfig.metadata as Record<string, unknown>, {
          document_class: documentClass,
        })
      );
    }

    const useCases =
      (config.use_cases as Record<string, Record<string, unknown>>) ?? {};
    for (const [useCase, useCaseConfig] of Object.entries(useCases)) {
      appendScope(
        `use-case-${useCase}`,
        260,
        { use_case: [useCase] },
        (useCaseConfig.fields as Record<string, unknown>) ?? {},
        (useCaseConfig.description as string) ?? null,
        mergeMetadata(useCaseConfig.metadata as Record<string, unknown>, {
          use_case: useCase,
        })
      );
    }

    const processors =
      (config.processors as Record<string, Record<string, unknown>>) ?? {};
    for (const [processorId, processorConfig] of Object.entries(processors)) {
      appendScope(
        `processor-${processorId}`,
        300,
        { processor_id: [processorId] },
        (processorConfig.fields as Record<string, unknown>) ?? {},
        (processorConfig.description as string) ?? null,
        mergeMetadata(processorConfig.metadata as Record<string, unknown>, {
          processor_id: processorId,
        })
      );

      const processorClasses =
        (processorConfig.document_classes as Record<
          string,
          Record<string, unknown>
        >) ?? {};
      for (const [documentClass, docConfig] of Object.entries(
        processorClasses
      )) {
        appendScope(
          `processor-${processorId}-class-${documentClass}`,
          340,
          {
            processor_id: [processorId],
            document_class: [documentClass],
          },
          (docConfig.fields as Record<string, unknown>) ?? {},
          (docConfig.description as string) ?? null,
          mergeMetadata(docConfig.metadata as Record<string, unknown>, {
            processor_id: processorId,
            document_class: documentClass,
          })
        );
      }

      const processorUseCases =
        (processorConfig.use_cases as Record<string, Record<string, unknown>>) ??
        {};
      for (const [useCase, useCaseConfig] of Object.entries(processorUseCases)) {
        appendScope(
          `processor-${processorId}-use-case-${useCase}`,
          345,
          {
            processor_id: [processorId],
            use_case: [useCase],
          },
          (useCaseConfig.fields as Record<string, unknown>) ?? {},
          (useCaseConfig.description as string) ?? null,
          mergeMetadata(useCaseConfig.metadata as Record<string, unknown>, {
            processor_id: processorId,
            use_case: useCase,
          })
        );
      }

      const processorBusinessAreas =
        (processorConfig.business_areas as Record<
          string,
          Record<string, unknown>
        >) ?? {};
      for (const [businessArea, businessConfig] of Object.entries(
        processorBusinessAreas
      )) {
        appendScope(
          `processor-${processorId}-business-area-${businessArea}`,
          350,
          {
            processor_id: [processorId],
            business_area: [businessArea],
          },
          (businessConfig.fields as Record<string, unknown>) ?? {},
          (businessConfig.description as string) ?? null,
          mergeMetadata(businessConfig.metadata as Record<string, unknown>, {
            processor_id: processorId,
            business_area: businessArea,
          })
        );

        const docClasses =
          (businessConfig.document_classes as Record<
            string,
            Record<string, unknown>
          >) ?? {};
        for (const [documentClass, docConfig] of Object.entries(docClasses)) {
          appendScope(
            `processor-${processorId}-business-area-${businessArea}-class-${documentClass}`,
            360,
            {
              processor_id: [processorId],
              business_area: [businessArea],
              document_class: [documentClass],
            },
            (docConfig.fields as Record<string, unknown>) ?? {},
            (docConfig.description as string) ?? null,
            mergeMetadata(docConfig.metadata as Record<string, unknown>, {
              processor_id: processorId,
              business_area: businessArea,
              document_class: documentClass,
            })
          );
        }

        const useCaseSection =
          (businessConfig.use_cases as Record<string, Record<string, unknown>>) ??
          {};
        for (const [useCase, useCaseConfig] of Object.entries(useCaseSection)) {
          appendScope(
            `processor-${processorId}-business-area-${businessArea}-use-case-${useCase}`,
            365,
            {
              processor_id: [processorId],
              business_area: [businessArea],
              use_case: [useCase],
            },
            (useCaseConfig.fields as Record<string, unknown>) ?? {},
            (useCaseConfig.description as string) ?? null,
            mergeMetadata(useCaseConfig.metadata as Record<string, unknown>, {
              processor_id: processorId,
              business_area: businessArea,
              use_case: useCase,
            })
          );
        }
      }
    }
  }

  private createFieldRulesFromMap(
    scopeId: string,
    fieldMap: Record<string, unknown>
  ): FieldRuleDefinition[] {
    const fieldRules: FieldRuleDefinition[] = [];

    for (const [fieldType, definitionRaw] of Object.entries(fieldMap ?? {})) {
      if (!definitionRaw || typeof definitionRaw !== "object") {
        continue;
      }
      const definition = definitionRaw as Record<string, unknown>;
      const ruleId = buildScopeId([scopeId, fieldType]);
      const fieldTypes =
        (definition.field_types as string[]) ??
        (definition.fieldTypes as string[]) ??
        [fieldType === "*" ? "*" : fieldType];

      const rule = new FieldRuleDefinition({
        ruleId,
        fieldTypes,
        presets:
          (definition.presets as string[]) ??
          (definition.preset_ids as string[]) ??
          [],
        cleaningSteps:
          (definition.cleaning_steps as CleaningStepConfig[]) ??
          (definition.cleaningSteps as CleaningStepConfig[]) ??
          [],
        validationChecks:
          (definition.validation_checks as ValidationCheckConfig[]) ??
          (definition.validationChecks as ValidationCheckConfig[]) ??
          [],
        mergeStrategy:
          (definition.merge_strategy as MergeStrategy) ??
          definition.mergeStrategy,
        options:
          (definition.options as Record<string, unknown>) ?? {},
        description: (definition.description as string) ?? null,
        metadata:
          (definition.metadata as Record<string, unknown>) ?? {},
      });

      rule.fieldTypes.forEach((field) => this.configuredFieldTypes.add(field));
      fieldRules.push(rule);
    }

    return fieldRules;
  }
}

export const frontendValidationRuleEngine = new FrontendValidationRuleEngine();

export interface EntitiesValidationOptions extends ConfigLoadParams {
  entities: any[];
  context: ValidationContext;
}

export interface EntitiesValidationResult {
  cleanedEntities: any[];
  fieldResults: FieldValidationResponse[];
  errors: FieldValidationResponse[];
  warnings: FieldValidationResponse[];
  configVersion?: string;
}

const cloneEntity = (entity: any): any =>
  entity ? JSON.parse(JSON.stringify(entity)) : entity;

const validateEntityTree = (
  entity: any,
  engine: FrontendValidationRuleEngine,
  context: ValidationContext,
  collector: FieldValidationResponse[]
): any => {
  if (!entity || typeof entity !== "object") {
    return entity;
  }

  const cloned = cloneEntity(entity);
  const fieldType = typeof entity.type === "string" ? entity.type : "";
  const mentionText =
    entity.mentionText != null
      ? String(entity.mentionText)
      : entity.mention_text != null
        ? String(entity.mention_text)
        : "";

  if (fieldType && mentionText) {
    const result = engine.validateField(fieldType, mentionText, context);
    collector.push(result);
    cloned.mentionText = result.cleanedValue;
    if ("mention_text" in cloned || entity.mention_text !== undefined) {
      cloned.mention_text = result.cleanedValue;
    }
  }

  if (Array.isArray(entity.properties)) {
    cloned.properties = entity.properties.map((child: unknown) =>
      validateEntityTree(child, engine, context, collector)
    );
  }

  return cloned;
};

export const validateEntitiesWithRules = async (
  options: EntitiesValidationOptions
): Promise<EntitiesValidationResult> => {
  const { entities, context, accessToken, idToken, environment, projectId } = options;
  await frontendValidationRuleEngine.ensureConfigLoaded({
    accessToken,
    idToken,
    environment,
    projectId,
  });

  const results: FieldValidationResponse[] = [];
  const cleanedEntities = entities.map((entity) =>
    validateEntityTree(entity, frontendValidationRuleEngine, context, results)
  );

  const errors = results.filter((result) => !result.isValid);
  const warnings = results.filter(
    (result) => result.isValid && result.warnings.length > 0
  );

  return {
    cleanedEntities,
    fieldResults: results,
    errors,
    warnings,
    configVersion: frontendValidationRuleEngine.getConfigVersion(),
  };
};
